import React from 'react';
import styled from 'styled-components';

interface BottomNavigationProps {
  activeTab: 'map' | 'demands' | 'contacts' | 'account';
  onTabChange: (tab: 'map' | 'demands' | 'contacts' | 'account') => void;
  position?: 'center' | 'right';
  visible?: boolean;
}

const Nav = styled.nav<{ $position?: 'center' | 'right'; $visible?: boolean }>`
  position: fixed;
  bottom: 30px;
  ${props => props.$position === 'right' ? `
    right: 30px;
    left: auto;
    transform: ${props.$visible ? 'none' : 'translateY(150%)'};
  ` : `
    left: 50%;
    transform: ${props.$visible ? 'translateX(-50%)' : 'translate(-50%, 150%)'};
  `}
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 50px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  display: flex;
  gap: 4px;
  padding: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    bottom: 0;
    left: 0 !important;
    right: 0;
    width: 100%;
    transform: ${props => props.$visible ? 'none' : 'translateY(100%)'} !important;
    border-radius: 0;
    border: none;
    border-top: 1px solid rgba(226, 232, 240, 0.8);
    box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.08);
    padding: 8px 4px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    gap: 0;
    justify-content: space-around;
    background: rgba(255, 255, 255, 0.97);
  }
`;

const NavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  background: ${props => props.$active ? 'rgba(var(--accent-rgb), 0.1)' : 'transparent'};
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    padding: 8px 4px;
    border-radius: 12px;
    background: ${props => props.$active ? 'rgba(var(--accent-rgb), 0.1)' : 'transparent'};
  }
  
  svg {
    width: 22px;
    height: 22px;
    color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
    transition: all 0.2s ease;
  }
  
  span {
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
    transition: all 0.2s ease;
  }
  
  &:hover {
    background: ${props => props.$active ? 'rgba(var(--accent-rgb), 0.15)' : 'rgba(148, 163, 184, 0.1)'};
    
    svg {
      color: ${props => props.$active ? 'var(--accent-dark)' : '#475569'};
      transform: translateY(-2px);
    }
    
    span {
      color: ${props => props.$active ? 'var(--accent-dark)' : '#475569'};
    }
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const BottomNavigation: React.FC<BottomNavigationProps> = ({ activeTab, onTabChange, position = 'center', visible = true }) => {
  return (
    <Nav $position={position} $visible={visible}>
      <NavItem $active={activeTab === 'map'} onClick={() => onTabChange('map')}>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <span>Cidades</span>
      </NavItem>
      
      <NavItem $active={activeTab === 'demands'} onClick={() => onTabChange('demands')}>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        <span>Demandas</span>
      </NavItem>
      
      <NavItem $active={activeTab === 'contacts'} onClick={() => onTabChange('contacts')}>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
        </svg>
        <span>Contatos</span>
      </NavItem>
      
      <NavItem $active={activeTab === 'account'} onClick={() => onTabChange('account')}>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
        </svg>
        <span>Configurações</span>
      </NavItem>
    </Nav>
  );
};

export default BottomNavigation;
